/**
 * The user-selection rule for the New Access screen, in one place.
 *
 * It used to live in two that disagreed. Every checkbox rendered `defaultChecked` — a default
 * owned by the DOM — while the parent's selection state started `undefined` and `submit` fell
 * back to "all clients" whenever it still was. Unticking one person turned `undefined` into
 * `[]`, which is no longer the fallback, and a box left ticked never fired a change event to
 * put its owner back. So the grant silently reached nobody while every remaining box still
 * looked ticked. Holding the rule here, and deriving each box's `checked` from its result,
 * means what you see ticked is what gets granted.
 *
 * Takes the shape it needs rather than the `Client` model, so it stays free of Firebase and
 * is testable on its own.
 */
export function reconcileSelection(
  previous: string[] | undefined,
  clients: Array<{ docId: string }>
): string[] {
  // First list in: select everyone. Most grants go to the whole company, and the old screen
  // promised that default visually — it is now held as state so the two cannot drift apart.
  if (previous === undefined) return clients.map((client) => client.docId);

  // A later snapshot (someone added or removed on the company while the screen is open) must
  // not overwrite a choice the employee has already made: keep it, and drop only ids that no
  // longer exist. A newly added user therefore appears unticked — visibly not selected,
  // rather than silently swept into the grant.
  const stillPresent = previous.filter(
    (id) => clients.some((client) => client.docId === id)
  );

  // Preserve identity when nothing was dropped, so a no-op snapshot causes no re-render.
  return stillPresent.length === previous.length ? previous : stillPresent;
}
